/**
 * The admin console's only door to the platform: `/api/v1/*` on its own
 * origin, which the admin BFF forwards with the session's token. The browser
 * never sees a token.
 *
 * Every call carries `X-Genworx-Request`. The BFF refuses a state-changing call
 * without it, which is what stops another *.genworx.cloud page — same site,
 * different origin — from driving the console with an admin's cookie.
 */

import type { ApiRefusal } from 'zitimail-shared-types';

import { fetchOrExplain, parseJson, plainFailure } from '../api/http';

/** Fired when an API call finds the session gone. */
export const SESSION_ENDED = 'genworx:session-ended';

export class ApiError extends Error {
  /** The refusal as the API sent it, for routes that explain more than a message. */
  declare readonly body?: unknown;

  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
    if (body !== undefined) {
      this.body = body;
    }
  }
}

export async function api<T>(
  path: string,
  init: { method?: string; body?: unknown; signal?: AbortSignal } = {},
): Promise<T> {
  const response = await fetchOrExplain(
    `/api/v1${path}`,
    {
      method: init.method ?? 'GET',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'X-Genworx-Request': '1',
        ...(init.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      },
      ...(init.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
      ...(init.signal ? { signal: init.signal } : {}),
    },
    'platform',
  );
  const payload = parseJson(await response.text());
  if (response.status === 401) {
    // The BFF session is over (idle limit, sign-out elsewhere, a disabled
    // account). The console re-checks it and shows sign-in, rather than every
    // panel reporting its own failure.
    window.dispatchEvent(new Event(SESSION_ENDED));
  }
  if (!response.ok) {
    const refusal = (payload ?? {}) as Partial<ApiRefusal>;
    throw new ApiError(
      response.status,
      refusal.error ?? `http_${response.status}`,
      refusal.message ?? refusal.error ?? plainFailure('platform', response.status),
      payload,
    );
  }
  return payload as T;
}

export function describeError(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return 'Your session has ended. Sign in again.';
    }
    return error.message;
  }
  return error instanceof Error ? error.message : 'Something went wrong.';
}
